import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ProfileService } from '../../services/profile.service';
import { UpdateContactInfoDto } from '@usf/user-types/user';
import { catchError, take } from 'rxjs/operators';
import { UserDataService } from 'src/app/user/services/user-data.service';
import { firstValueFrom, of, Subscription } from 'rxjs';
import { ModalService } from 'src/app/shared/services/modal/modal.service';
import { ModalController, PopoverController } from '@ionic/angular';
import { SimpleSpinnerModalComponent } from 'src/app/shared/components/simple-spinner-modal/simple-spinner-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { I18nConfigService } from '@shared/services/i18n-config.service';
import {
  LOCAL_STORAGE_KEYS,
  PanAppState,
  PlatformService,
} from '@panamax/app-state';
import { ProfileAnalyticsService } from '@app/profile/analytics/profile-analytics.service';
import { FEATURES } from '@shared/constants/splitio-features';

@Component({
  selector: 'app-profile-contact-card',
  templateUrl: './profile-contact-card.component.html',
  styleUrls: ['./profile-contact-card.component.scss'],
})
export class ProfileContactCardComponent implements OnDestroy {
  @Input() isMobile: boolean;
  isLoading: boolean;
  @Input() userKind: string;
  @Input() ecUserId: number;
  @Output() showToast = new EventEmitter();
  @Output() setNewOriginal = new EventEmitter();
  @Input() contactForm: UntypedFormGroup;
  updateContactInfo$: Subscription;
  spinnerModal?: HTMLIonModalElement;
  isSaving?: boolean = false;
  isModalOpen = false;
  selectedProfileLanguage = '';

  //Feature flags
  primaryLanguageSelectionFlag =
    FEATURES.split_global_primary_language_selection.name;

  constructor(
    private profileService: ProfileService,
    private userDataService: UserDataService,
    private modalController: ModalController,
    private modalService: ModalService,
    public translateService: TranslateService,
    private popoverController: PopoverController,
    private i18nConfigService: I18nConfigService,
    readonly platformService: PlatformService,
    private analyticsService: ProfileAnalyticsService,
    private panAppState: PanAppState,
  ) {}

  ngOnDestroy() {
    if (this.updateContactInfo$) {
      this.updateContactInfo$.unsubscribe();
    }
  }

  validateSubmit() {
    if (this.ecUserId && !this.contactForm.invalid && !this.isSaving) {
      const updatedInfo: UpdateContactInfoDto = {
        ecUserId: this.ecUserId,
        firstName: this.contactForm.controls.firstName.value,
        lastName: this.contactForm.controls.lastName.value,
        orgName: this.contactForm.controls.organizationName.value,
        email: this.contactForm.controls.notificationEmail.value,
      } as UpdateContactInfoDto;
      this.isSaving = true;
      this.showModal();
      this.setContactInformation(updatedInfo);
    }
  }

  setContactInformation(newInformation: UpdateContactInfoDto) {
    this.updateContactInfo$ = this.userDataService
      .updateContactInformation(newInformation)
      .pipe(
        take(1),
        catchError(() => {
          this.handleError();
          return of(undefined);
        }),
      )
      .subscribe(result => {
        if (result) {
          if (result.code === 200) {
            if (newInformation.email?.trim() !== '') {
              this.updateUserStateEmail(newInformation.email);
            }
            setTimeout(() => {
              this.contactForm.markAsPristine();
              this.profileService.setProfileUnchanged(true);
              this.setNewOriginal.emit();
              this.showToast.emit({
                toastMessage: 'i18n.profilePage.toast.contactSaveSuccess',
                type: 'success',
              });
              this.spinnerModal?.dismiss();
            }, 3500);
          } else {
            this.handleError();
            this.spinnerModal?.dismiss();
          }
          this.isSaving = false;
        } else {
          this.handleError();
          this.spinnerModal?.dismiss();
          this.isSaving = false;
        }
      });
  }

  async showModal() {
    this.modalService.setModalOptions(
      false,
      SimpleSpinnerModalComponent,
      {
        spinnerType: 'circles',
      },
      'simple-spinner-modal',
    );
    this.spinnerModal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    return await this.spinnerModal.present();
  }

  handleError() {
    this.showToast.emit({
      toastMessage: 'i18n.profilePage.toast.contactSaveError',
      type: 'error',
    });
  }

  validateValue() {
    this.profileService.setProfileUnchanged(false);
  }

  isSaveDisable() {
    return !this.contactForm.dirty || this.contactForm.invalid || this.isSaving;
  }

  pressEnterKey() {
    !this.isSaving ? this.validateSubmit() : null;
    return false;
  }

  updateLanguage(newLanguage: string) {
    this.translateService.use(newLanguage);
    localStorage.setItem('appLanguage', newLanguage);
    localStorage.setItem(LOCAL_STORAGE_KEYS.displayLanguage, newLanguage);
    this.selectedProfileLanguage = this.translateService.currentLang;
    this.analyticsService.trackLanguageOverrideSelection(
      newLanguage === 'en' ? 'English' : 'Spanish',
    );
  }

  useBrowserDefaultLanguage() {
    this.i18nConfigService.useBrowserDefault();
    this.selectedProfileLanguage = this.translateService.instant(
      'i18n.profilePage.contact.none',
    );
    this.analyticsService.trackLanguageOverrideSelection('None');
  }

  async openPopover(event: any) {
    const buttons: any[] = [
      {
        text: 'i18n.profilePage.contact.none',
        handler: () => {
          this.useBrowserDefaultLanguage();
          this.dismissClick();
        },
      },
      {
        text: 'i18n.profilePage.contact.english',
        handler: () => {
          this.updateLanguage('en');
          this.dismissClick();
        },
      },
      {
        text: 'i18n.profilePage.contact.spanish',
        handler: () => {
          this.updateLanguage('es');
          this.dismissClick();
        },
      },
    ];
    const popover = await this.popoverController.create({
      component: EllipsisOptionsPopoverComponent,
      event,
      componentProps: {
        buttons,
      },
      cssClass: 'ellipsis-options-popover-desktop',
      mode: 'ios',
      showBackdrop: false,
    });
    await popover.present();
  }

  async dismissClick() {
    await this.popoverController.dismiss();
  }

  handleLanguageChange(ev) {
    const selectedLanguage = ev.target.value;

    if (selectedLanguage === 'es' || selectedLanguage === 'en') {
      this.updateLanguage(selectedLanguage);
    } else {
      this.useBrowserDefaultLanguage();
    }
  }

  setOpen(isOpen: boolean) {
    this.isModalOpen = isOpen;
  }

  get selectedProfileDisplayLang(): string {
    switch (localStorage.getItem('appLanguage')) {
      case 'en':
        return 'English';
      case 'es':
        return 'Español';
      default:
        return this.translateService.instant('i18n.profilePage.contact.none');
    }
  }

  get touchPopoverReady(): boolean {
    return !!document.getElementById('open-modal');
  }

  private updateUserStateEmail(email: string) {
    firstValueFrom(this.panAppState.user$).then(user => {
      this.panAppState.persistUser({
        userId: user.userId,
        ecomUserId: user.ecomUserId,
        email,
      });
    });
  }
}
